import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { useLocale } from './i18n';
import { AppShell, PublicShell } from './layouts/AppShell';
import { ItemPage } from './pages/ItemPage';
import { LandingPage } from './pages/LandingPage';
import { LibraryPage } from './pages/LibraryPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { NewPlaylistPage, PlaylistsPage } from './pages/PlaylistsPage';
import { PlaylistEditorPage } from './pages/PlaylistEditorPage';
import { PlaylistViewPage } from './pages/PlaylistViewPage';
import { FeedPage } from './pages/FeedPage';
import { OwnProfilePage, UserPage } from './pages/UserPage';
import { SearchPage } from './pages/SearchPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { Loading } from './components/ui';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="page">
        <Loading rows={4} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

export function App() {
  // Every screen below reads the language through the plain t(); this subscription is what re-renders them.
  useLocale();
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        <Route element={<PublicShell />}>
          <Route path="/u/:username" element={<UserPage />} />
          <Route path="/p/:id" element={<PlaylistViewPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/app" element={<FeedPage />} />
          <Route path="/app/library" element={<LibraryPage />} />
          <Route path="/app/items/:id" element={<ItemPage />} />
          <Route path="/app/playlists" element={<PlaylistsPage />} />
          <Route path="/app/playlists/new" element={<NewPlaylistPage />} />
          <Route path="/app/playlists/:id" element={<PlaylistViewPage />} />
          <Route path="/app/playlists/:id/edit" element={<PlaylistEditorPage />} />
          <Route path="/app/profile" element={<OwnProfilePage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/search" element={<SearchPage />} />
        </Route>
      </Routes>
    </>
  );
}
